import { buildInvestigateEvidencePath } from './overviewSiteConditionSummary';
import { buildScanVerifyPath } from '../acceptance/scanVerifyNavigation';

export const CONDITION_LIFECYCLE_UI_PATH = '/acceptance/lifecycle';

export type ConditionLifecycleFocus =
  | 'immediate'
  | 'pattern'
  | 'pattern-membership'
  | 'control'
  | 'since'
  | 'scan'
  | 'before'
  | 'after'
  | 'evidence'
  | 'visual';

export type ConditionLifecycleRouteArgs = {
  siteId: string;
  conditionId: string;
  areaId?: string | null;
  zoneId?: string | null;
  cameraId?: string | null;
  orgId?: string | null;
  focus?: ConditionLifecycleFocus | null;
};

function text(value: unknown): string {
  return String(value || '').trim();
}

function appendScope(params: URLSearchParams, args: ConditionLifecycleRouteArgs): void {
  const siteId = text(args.siteId);
  const conditionId = text(args.conditionId);
  const areaId = text(args.areaId);
  const zoneId = text(args.zoneId);
  const cameraId = text(args.cameraId);
  const orgId = text(args.orgId);
  if (siteId) params.set('site_id', siteId);
  if (conditionId) params.set('condition_id', conditionId);
  if (areaId) params.set('area_id', areaId);
  if (zoneId) params.set('zone_id', zoneId);
  if (cameraId) params.set('camera_id', cameraId);
  if (orgId) params.set('org_id', orgId);
}

/** Canonical dashboard drill-down. Never an API URL. */
export function buildConditionLifecyclePath(args: ConditionLifecycleRouteArgs): string {
  const siteId = text(args.siteId);
  const conditionId = text(args.conditionId);
  if (!siteId || !conditionId) return CONDITION_LIFECYCLE_UI_PATH;
  const params = new URLSearchParams();
  appendScope(params, args);
  if (args.focus) params.set('focus', args.focus);
  return `${CONDITION_LIFECYCLE_UI_PATH}?${params.toString()}`;
}

export function isConditionLifecycleUiPath(path: string | null | undefined): boolean {
  const value = text(path);
  if (!value) return false;
  const pathname = value.split('?')[0];
  return pathname === CONDITION_LIFECYCLE_UI_PATH || pathname.startsWith(`${CONDITION_LIFECYCLE_UI_PATH}/`);
}

export function isApiLifecycleHref(href: string | null | undefined): boolean {
  const value = text(href);
  if (!value) return false;
  return /\/api\/v1\/sites\/[^/]+\/condition-lifecycle(?:\?|$)/.test(value);
}

export function buildLifecycleImmediateIncidentPath(args: ConditionLifecycleRouteArgs & {
  incidentId: string;
  incidentType?: string | null;
  conditionFamily?: string | null;
}): string {
  const incidentId = text(args.incidentId);
  if (!incidentId || !text(args.siteId)) return buildConditionLifecyclePath({ ...args, focus: 'immediate' });
  return buildInvestigateEvidencePath({
    siteId: args.siteId,
    zoneId: args.zoneId,
    cameraId: args.cameraId,
    incidentType: args.incidentType,
    conditionId: args.conditionId,
    conditionFamily: args.conditionFamily,
    incidentId,
    review: true,
  });
}

export function buildLifecyclePatternIncidentPath(args: ConditionLifecycleRouteArgs & {
  incidentId: string;
  incidentType?: string | null;
  conditionFamily?: string | null;
}): string {
  const incidentId = text(args.incidentId);
  if (!incidentId || !text(args.siteId)) return buildConditionLifecyclePath({ ...args, focus: 'pattern' });
  return buildInvestigateEvidencePath({
    siteId: args.siteId,
    zoneId: args.zoneId,
    cameraId: args.cameraId,
    incidentType: args.incidentType,
    conditionId: args.conditionId,
    conditionFamily: args.conditionFamily,
    incidentId,
    review: true,
  });
}

export function buildLifecyclePatternMembershipPath(args: ConditionLifecycleRouteArgs): string {
  return buildConditionLifecyclePath({ ...args, focus: 'pattern-membership' });
}

export function buildLifecycleVisualPath(args: ConditionLifecycleRouteArgs): string {
  return buildConditionLifecyclePath({ ...args, focus: 'visual' });
}

/**
 * Control hop. Prefer the canonical lifecycle page (EPIC402 revision).
 * A Safety Case CCR path is only used when explicitly supplied as a known record —
 * callers must not invent one from controlRevisionId.
 */
export function buildLifecycleControlPath(args: ConditionLifecycleRouteArgs & {
  controlRecordPath?: string | null;
  hasEpic402Control?: boolean;
}): string {
  if (args.hasEpic402Control) {
    return buildConditionLifecyclePath({ ...args, focus: 'control' });
  }
  const recordPath = text(args.controlRecordPath);
  if (recordPath && !isApiLifecycleHref(recordPath)) return recordPath;
  return buildConditionLifecyclePath({ ...args, focus: 'control' });
}

export function buildLifecycleScanVerifyPath(args: ConditionLifecycleRouteArgs & {
  reportId?: string | null;
  safetyCaseId?: string | null;
}): string {
  const reportId = text(args.reportId);
  if (!reportId) return buildConditionLifecyclePath({ ...args, focus: 'scan' });
  return buildScanVerifyPath({
    reportId,
    orgId: args.orgId,
    siteId: args.siteId,
    safetyCaseId: args.safetyCaseId,
    conditionId: args.conditionId,
  });
}

export function buildLifecycleBeforeReportPath(args: ConditionLifecycleRouteArgs & {
  reportId?: string | null;
  safetyCaseId?: string | null;
}): string {
  const reportId = text(args.reportId);
  if (!reportId) return buildConditionLifecyclePath({ ...args, focus: 'before' });
  return buildScanVerifyPath({
    reportId,
    orgId: args.orgId,
    siteId: args.siteId,
    safetyCaseId: args.safetyCaseId,
    conditionId: args.conditionId,
  });
}

export function buildLifecycleAfterReportPath(args: ConditionLifecycleRouteArgs & {
  reportId?: string | null;
  safetyCaseId?: string | null;
}): string {
  const reportId = text(args.reportId);
  if (!reportId) return buildConditionLifecyclePath({ ...args, focus: 'after' });
  return buildScanVerifyPath({
    reportId,
    orgId: args.orgId,
    siteId: args.siteId,
    safetyCaseId: args.safetyCaseId,
    conditionId: args.conditionId,
  });
}

export function buildLifecycleEvidencePath(args: ConditionLifecycleRouteArgs & {
  reportId?: string | null;
  incidentId?: string | null;
  incidentType?: string | null;
  conditionFamily?: string | null;
  safetyCaseId?: string | null;
}): string {
  const reportId = text(args.reportId);
  if (reportId) {
    return buildScanVerifyPath({
      reportId,
      orgId: args.orgId,
      siteId: args.siteId,
      safetyCaseId: args.safetyCaseId,
      incidentId: args.incidentId,
      conditionId: args.conditionId,
    });
  }
  const incidentId = text(args.incidentId);
  if (incidentId) {
    return buildInvestigateEvidencePath({
      siteId: args.siteId,
      zoneId: args.zoneId,
      cameraId: args.cameraId,
      incidentType: args.incidentType,
      conditionId: args.conditionId,
      conditionFamily: args.conditionFamily,
      incidentId,
      review: true,
    });
  }
  return buildConditionLifecyclePath({ ...args, focus: 'evidence' });
}
